import {
  Box,
  Button,
  Collapse,
  HStack,
  Image,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import getImageSize from "@splitfire-agency/raiden-library/dist/libraries/utils/getImageSize";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { RESOURCES_MEDIA_TYPES } from "../../../constants/resources";
import useCoversFile from "../../../hooks/covers/useFile";
import FormErrorMessageRHF from "../../ReactHookForm/FormErrorMessageRHF";
import FormErrorObserverRHF from "../../ReactHookForm/FormErrorObserverRHF";
import Splash from "../../Splash";

export default function CoversDragAndDrop(props) {
  const { constraints, minWidth, minHeight, coverName, transformationsName } =
    props;

  const toast = useToast();

  const intl = useIntl();

  const form = useFormContext();
  const { watch } = form;

  const [dragHover, setDragHover] = useState(false);

  /** @type {import("react").MutableRefObject<HTMLDivElement | null>} */
  const $dropZone = useRef(null);
  /** @type {import("react").MutableRefObject<HTMLInputElement | null>} */
  const $input = useRef(null);

  const strMimeTypes = constraints.mime_types
    .map(function (mimeType) {
      return RESOURCES_MEDIA_TYPES?.[mimeType]?.friendlyName ?? mimeType;
    })
    .join(", ");

  const setFile = useCallback(
    /**
     * Met à jour le fichier si les contraintes sont respectées.
     * @param {File} file
     */
    async function (file) {
      if (!file) {
        return;
      }

      let ok = true;

      if (file.size > constraints.max_filesize * 1024) {
        toast({
          status: "warning",
          title: intl.formatMessage(
            {
              defaultMessage:
                "La taille de l’image ne doit pas être supérieure à {maxFilesize} ko.",
            },
            {
              maxFilesize: constraints.max_filesize,
            },
          ),
          position: "bottom-right",
        });

        ok = false;
      }

      if (!constraints.mime_types.includes(file.type)) {
        toast({
          status: "warning",
          title: intl.formatMessage(
            {
              defaultMessage:
                "Le format de l’image doit être du type {mimeTypes}.",
            },
            {
              mimeTypes: strMimeTypes,
            },
          ),
          position: "bottom-right",
        });

        ok = false;
      }

      const { width, height } = await getImageSize({ file });

      if (width < minWidth) {
        toast({
          status: "warning",
          title: intl.formatMessage(
            {
              defaultMessage:
                "La largeur minimale de l’image doit être de {minWidth}px, votre image a une largeur de {width}px.",
            },
            {
              minWidth,
              width,
            },
          ),
          position: "bottom-right",
          duration: 10000,
          isClosable: true,
        });
        ok = false;
      }

      if (height < minHeight) {
        toast({
          status: "warning",
          title: intl.formatMessage(
            {
              defaultMessage:
                "La hauteur minimale de l’image doit être de {minHeight}px, votre image a une hauteur de {height}px.",
            },
            {
              minHeight,
              height,
            },
          ),
          position: "bottom-right",
          duration: 10000,
          isClosable: true,
        });
        ok = false;
      }

      if (ok) {
        form.setValue(coverName, file, { shouldDirty: true });
        form.setValue(transformationsName, "", { shouldDirty: true });
        form.clearErrors(coverName);
      }
    },
    [
      coverName,
      transformationsName,
      toast,
      intl,
      strMimeTypes,
      constraints.max_filesize,
      constraints.mime_types,
      minWidth,
      minHeight,
      form,
    ],
  );

  useEffect(
    function () {
      /**
       * Gère l’entrée d’un élément de la zone de drop.
       * @param {DragEvent} event
       */
      function handleDragEnter(event) {
        if ($dropZone.current === event.target) {
          setDragHover(true);
        }
      }

      /**
       * Gère la sortie d’un élément de la zone de drop.
       * @param {DragEvent} event
       */
      function handleDragLeave(event) {
        if ($dropZone.current === event.target) {
          setDragHover(false);
        }
      }

      /**
       * Gère le laché dans la zone de drop.
       */
      function handleDrop(event) {
        event.preventDefault();

        if ($dropZone.current === event.target) {
          setDragHover(false);

          const files = Array.from(event.dataTransfer.files);
          const file = files[0];

          setFile(file);
        }
      }

      /**
       * Gère le laché dans la zone de drop.
       * @param {DragEvent} event
       */
      function handleDrogOver(event) {
        event.preventDefault();
      }

      document.addEventListener("dragenter", handleDragEnter);
      document.addEventListener("dragleave", handleDragLeave);
      document.addEventListener("drop", handleDrop);
      document.addEventListener("dragover", handleDrogOver);

      return function () {
        document.removeEventListener("dragenter", handleDragEnter);
        document.removeEventListener("dragleave", handleDragLeave);
        document.removeEventListener("drop", handleDrop);
        document.removeEventListener("dragover", handleDrogOver);
      };
    },
    [setFile],
  );

  function handleChange(event) {
    const files = Array.from(event.currentTarget.files);

    const file = files[0];

    setFile(file);
  }

  const file = watch(coverName);
  const cover = useCoversFile(file);

  return (
    <VStack alignItems="stretch" w="full">
      <FormErrorObserverRHF
        name={coverName}
        render={({ hasError, error }) => (
          <Collapse in={hasError} unmountOnExit={true}>
            <FormErrorMessageRHF error={error} />
          </Collapse>
        )}
      />

      <Text color="gray.400" fontSize="0.875rem" mb="1rem">
        {intl.formatMessage(
          {
            defaultMessage:
              "Poids maximal : {maxFilesize} ko • Formats supportés : {mimeTypes}.",
          },
          {
            maxFilesize: constraints.max_filesize,
            mimeTypes: strMimeTypes,
          },
        )}
      </Text>

      <Box
        ref={$dropZone}
        borderWidth="0.125rem"
        borderStyle="dashed"
        borderColor={"gray.500"}
        transition="background-color 160ms linear 0ms"
        bgColor={dragHover ? "orange.100" : undefined}
        px={{ base: "1rem", md: "3.125rem" }}
        py="1rem">
        <input
          ref={$input}
          type="file"
          accept={constraints.mime_types.join(",")}
          style={{ width: 0, height: 0, opacity: 0, visibility: "hidden" }}
          onChange={handleChange}
        />

        <VStack pointerEvents="none">
          <Splash
            image={
              cover ? (
                <Box display="flex" justifyContent="center">
                  <Image
                    src={cover}
                    maxW="21.875rem"
                    maxH="12.3046875rem"
                    alt={intl.formatMessage({ defaultMessage: "Image" })}
                  />
                </Box>
              ) : (
                "default"
              )
            }
            title={intl.formatMessage({
              defaultMessage: "Ajoutez une image",
            })}
            description={intl.formatMessage(
              {
                defaultMessage:
                  "{hasCover, select, true {Photo actuelle} other {Vous n’avez pas encore ajouté de photo, faites glisser votre image ici.}}",
              },
              {
                hasCover: Boolean(cover),
              },
            )}
          />

          {cover ? (
            <HStack spacing="1rem">
              <Button
                variant="outline"
                pointerEvents="all"
                colorScheme="red"
                onClick={function () {
                  form.setValue(coverName, null, { shouldDirty: true });
                  form.setValue(`data.${coverName}`, null, {
                    shouldDirty: true,
                  });
                }}>
                {intl.formatMessage({
                  defaultMessage: "Supprimer",
                })}
              </Button>

              <Button
                colorScheme="darkGray"
                variant="outline"
                pointerEvents="all"
                onClick={function () {
                  $input.current?.click();
                }}>
                {intl.formatMessage({
                  defaultMessage: "Remplacer",
                })}
              </Button>
            </HStack>
          ) : (
            <Button
              onClick={function () {
                $input.current?.click();
              }}
              variant="outline"
              colorScheme="brandPrimary"
              pointerEvents="all">
              {intl.formatMessage({
                defaultMessage: "Chercher sur mon ordinateur",
              })}
            </Button>
          )}
        </VStack>
      </Box>
    </VStack>
  );
}

CoversDragAndDrop.propTypes = {
  constraints: PropTypes.object,
  minWidth: PropTypes.number,
  minHeight: PropTypes.number,
  coverName: PropTypes.string,
  transformationsName: PropTypes.string,
  form: PropTypes.object,
};

CoversDragAndDrop.defaultProps = {};
